import { ALERT_CONDITION_TYPES, METRIC_NAMES, WIDGET_LOGICAL_NAMES } from '@config/Consts';
import AlertModel from '@core/models/AlertModel';
import EventBusEmitter from '@core/event-bus/EventBusEmitter';
import Utils from '@core/services/alerts/bots/utils';

/**
 * @name notificationMetricHandler
 * @description updates notification metrics for each agent record
 * Curried handler
 * 1. Function - Receives agent record {id, metrics}
 * 2. Function - Receives flag to add/remove alert & the reason to fire up the alert
 * @param botRecord {Object<id, metrics[]>}
 * @return {
 * (function({metric, reason: {Object}, addNotification?: {Boolean}}): void)|*
 * }
 * @type {notificationMetricHandler}
 */
// eslint-disable-next-line no-unused-vars
const notificationMetricHandler = botRecord => ({ metric, reason, addNotification }) => {
  // // get bot name
  const name = Utils.findMetricValue(botRecord.metrics, METRIC_NAMES.AGENT_NAME);
  // // get i18n translated messages:
  // // -general goes to notifications column
  // // -personal goes to alertsHistory
  const { general, personal } = Utils.i18nHandlers[metric.metric]({
    ...reason,
    agentName: name,
  });
  // is alert is active and there is a record in reasons array
  const hasAlert = botRecord.metrics[0].reasons.includes(general);
  if (!hasAlert && addNotification) {
    // eslint-disable-next-line no-param-reassign
    botRecord.metrics[0].score += 1;
    // eslint-disable-next-line no-param-reassign
    botRecord.metrics[0].reasons = [
      ...botRecord.metrics[0].reasons,
      general,
    ];
    EventBusEmitter.emit('alertsHistory:add', [
      new AlertModel({
        origin: `${WIDGET_LOGICAL_NAMES.BOTS}__${personal}`,
        message: personal,
        showAlert: true,
      })]);
  }
};

/**
 * @name handleStandardMetric
 * @description Handles standard metric alerts evaluation
 * @param metric {Object<metric, score>}
 * @param alertData {Object}
 * @param cb {notificationMetricHandler}
 */
function handleStandardMetric({ metric, alertData, cb }) {
  // if metric score is other than NULL
  if (metric.score !== null) {
    const { thresholdTwo, thresholdOne } = alertData.values;
    const { score } = metric;
    //
    const alerts = {
      [thresholdOne.condition]: Utils.getIsAlertActiveForStandardMetric(thresholdOne, score),
      ...(
        thresholdTwo &&
        { [thresholdTwo.condition]: Utils.getIsAlertActiveForStandardMetric(thresholdTwo, score) }
      ),
    };
    // set isAlert for UI
    // eslint-disable-next-line no-param-reassign
    metric.isAlert = Object.values(alerts).some(a => a.isAlert);
    //
    // fire up notification updates - Alerts History
    if (alerts[ALERT_CONDITION_TYPES.BELOW] && alerts[ALERT_CONDITION_TYPES.BELOW].isAlert) {
      cb({
        metric,
        addNotification: alerts[ALERT_CONDITION_TYPES.BELOW].isAlert,
        reason: {
          condition: ALERT_CONDITION_TYPES.BELOW,
          value: alerts[ALERT_CONDITION_TYPES.BELOW].value,
        },
      });
    }
    // //
    if (alerts[ALERT_CONDITION_TYPES.ABOVE] && alerts[ALERT_CONDITION_TYPES.ABOVE].isAlert) {
      cb({
        metric,
        addNotification: alerts[ALERT_CONDITION_TYPES.ABOVE].isAlert,
        reason: {
          condition: ALERT_CONDITION_TYPES.ABOVE,
          value: alerts[ALERT_CONDITION_TYPES.ABOVE].value,
        },
      });
    }
  }
}

/**
 * @name handlePendingMetric
 * @param metric
 * @param alertData
 * @param cb
 */
function handlePendingMetric({ metric, alertData, cb }) {
  return handleStandardMetric({ metric, alertData, cb });
}


/**
 * @name handlers
 * @description Alerts evaluation handlers map
 * @break
 * - CSAT
 * - STATUS DURATION
 */
const handlers = {

  [METRIC_NAMES.AGENT_VIEW_PENDING_RATE]: handlePendingMetric,
};

/**
 * @name evaluateMetrics
 * @description Evaluates agents for alerts configured
 * @param agentsRecords
 * @param alertsData
 * @return {*}
 */
const evaluateMetrics = async (agentsRecords, alertsData) => {
  const alertsConfigured = Utils.checkAlertsConfigured(alertsData);
  if (
    agentsRecords
    && !!agentsRecords.length
    && Object.values(alertsConfigured)
      .includes(true)
  ) {
    agentsRecords.forEach((agentRecord) => {
      // evaluate
      agentRecord.metrics.forEach((metric) => {
        // if alert configured for the metric
        if (alertsConfigured[metric.metric]) {
          // create handler payload
          const payload = {
            metric,
            alertData: alertsData[metric.metric],
            cb: notificationMetricHandler(agentRecord),
          };
          // add agent status when metric is currentStatusDuration
          if (metric.metric === METRIC_NAMES.AGENT_VIEW_AGENT_STATUS_DURATION) {
            payload.agentStatus = {
              status: Utils.findMetricValue(agentRecord.metrics, METRIC_NAMES.AGENT_VIEW_AGENT_STATUS),
              reason: Utils.findMetricValue(agentRecord.metrics, METRIC_NAMES.AGENT_VIEW_STATUS_REASON),
            };
          }
          // call handler with the payload
          handlers[metric.metric](payload);
        }
      });
      // end: evaluate
    });
  }
  return agentsRecords;
};


export const BotsAlertsService = {
  evaluateMetrics,

};
